/**
 * SnapshotViewerDialog — universal viewer for any saved-file snapshot kind.
 *
 * Opens when a user clicks a snapshot in SavedFilesDialog,
 * WorkspaceSavedFiles, or RestaurantSavedFiles. Renders a branded
 * header (hotel + restaurant when present) followed by a print-friendly
 * payload view. Exposes:
 *   • Print  — opens a hidden iframe and triggers window.print(). The
 *              iframe body mirrors the payload as a clean A4-landscape
 *              page so anything saved in the app can be printed in 1 click.
 *   • Restore into form — fires the legacy `open-snapshot` event so the
 *              InventoryTable (and future receivers) can hydrate from the
 *              snapshot's payload.
 *
 * Supported kinds: inventory, checklist, permit, insurer, subcontractor, schedule.
 */
import { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Printer, RotateCcw, FileText, FileSpreadsheet, CalendarDays } from 'lucide-react';
import { printHtml } from '../lib/print';
import { buildScheduleMatrix, buildScheduleMatrixHtml } from '../lib/scheduleExport';
import { absLogo } from '../lib/snapshotBranding';

const KIND_LABELS = {
  inventory: 'Inventory',
  checklist: 'Checklist',
  permit: 'Property Permit',
  insurer: 'Insurer Assets',
  subcontractor: 'Subcontractor Report',
  schedule: 'Schedule',
};

const RESTORABLE_KINDS = new Set(['inventory']);

function esc(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch (e) { return iso; }
}

// ───────────────────────── kind-specific renderers ─────────────────────────

function InventoryView({ payload }) {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  if (rows.length === 0) return <p className="text-sm text-gray-500">No rows saved.</p>;
  const cols = Array.from(
    rows.reduce((set, r) => {
      Object.keys(r || {}).forEach((k) => { if (k !== 'id' && k !== '_id') set.add(k); });
      return set;
    }, new Set()),
  );
  return (
    <div className="overflow-auto border border-gray-200 rounded-lg">
      <table className="w-full text-xs">
        <thead style={{ background: '#003DA5', color: 'white' }}>
          <tr>
            <th className="px-2 py-1.5 text-left font-bold">#</th>
            {cols.map((c) => (
              <th key={c} className="px-2 py-1.5 text-left font-bold capitalize">{c.replace(/_/g, ' ')}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className={idx % 2 ? 'bg-blue-50/30' : ''}>
              <td className="px-2 py-1 text-gray-500">{idx + 1}</td>
              {cols.map((c) => (
                <td key={c} className="px-2 py-1 text-gray-900">{String(r[c] ?? '')}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChecklistView({ payload }) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return (
    <div className="space-y-2">
      {payload?.subtitle && <p className="text-xs text-gray-600">{payload.subtitle}</p>}
      {payload?.workspaceLabel && (
        <p className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">{payload.workspaceLabel}</p>
      )}
      {items.length === 0 && <p className="text-sm text-gray-500">No items.</p>}
      <ul className="space-y-1.5">
        {items.map((it, idx) => {
          const text = typeof it === 'string' ? it : (it?.text || it?.label || '');
          const done = typeof it === 'object' ? !!it.done : false;
          return (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <span className={`inline-block w-4 h-4 mt-0.5 rounded border ${done ? 'bg-green-500 border-green-600' : 'bg-white border-gray-400'}`}>
                {done && <span className="text-white text-[10px] leading-3 pl-[2px]">✓</span>}
              </span>
              <span className="text-gray-800">{text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PermitView({ payload }) {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  if (rows.length === 0) return <p className="text-sm text-gray-500">No permits saved.</p>;
  return (
    <div className="overflow-auto border border-gray-200 rounded-lg">
      <table className="w-full text-xs">
        <thead style={{ background: '#003DA5', color: 'white' }}>
          <tr>
            <th className="px-2 py-1.5 text-left font-bold">#</th>
            <th className="px-2 py-1.5 text-left font-bold">Name</th>
            <th className="px-2 py-1.5 text-left font-bold">Valid from</th>
            <th className="px-2 py-1.5 text-left font-bold">Valid until</th>
            <th className="px-2 py-1.5 text-left font-bold">Issuing entity</th>
            <th className="px-2 py-1.5 text-left font-bold">Reference Nr</th>
            <th className="px-2 py-1.5 text-left font-bold">Scan</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className={idx % 2 ? 'bg-blue-50/30' : ''}>
              <td className="px-2 py-1 text-gray-500">{idx + 1}</td>
              <td className="px-2 py-1 text-gray-900">{r.name}</td>
              <td className="px-2 py-1 text-gray-900">{r.valid_from}</td>
              <td className="px-2 py-1 text-gray-900">{r.valid_until}</td>
              <td className="px-2 py-1 text-gray-900">{r.issuing_entity}</td>
              <td className="px-2 py-1 text-gray-900">{r.reference_nr}</td>
              <td className="px-2 py-1 text-blue-700">
                {r.scan_url ? <a href={r.scan_url.startsWith('http') ? r.scan_url : `${process.env.REACT_APP_BACKEND_URL}${r.scan_url}`} target="_blank" rel="noreferrer" className="underline">View scan</a> : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InsurerView({ payload }) {
  const photos = Array.isArray(payload?.photos) ? payload.photos : [];
  const documents = Array.isArray(payload?.documents) ? payload.documents : [];
  const links = Array.isArray(payload?.links) ? payload.links : [];
  const BE = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="space-y-4">
      {payload?.notes && (
        <div>
          <h3 className="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1">Notes</h3>
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{payload.notes}</p>
        </div>
      )}
      {photos.length > 0 && (
        <div>
          <h3 className="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Photos ({photos.length})</h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((u, i) => (
              <a key={i} href={u.startsWith('http') ? u : `${BE}${u}`} target="_blank" rel="noreferrer" className="block aspect-square rounded overflow-hidden border border-gray-200">
                <img src={u.startsWith('http') ? u : `${BE}${u}`} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}
      {documents.length > 0 && (
        <div>
          <h3 className="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Documents ({documents.length})</h3>
          <ul className="space-y-1">
            {documents.map((u, i) => (
              <li key={i} className="text-sm">
                <a href={u.startsWith('http') ? u : `${BE}${u}`} target="_blank" rel="noreferrer" className="text-blue-700 underline">{u.split('/').pop()}</a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {links.length > 0 && (
        <div>
          <h3 className="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1 flex items-center gap-1"><Link2 className="w-3 h-3" /> Links ({links.length})</h3>
          <ul className="space-y-1">
            {links.map((l, i) => {
              const url = typeof l === 'string' ? l : (l?.url || '');
              const label = typeof l === 'object' ? (l.label || url) : url;
              return (
                <li key={i} className="text-sm">
                  <a href={url} target="_blank" rel="noreferrer" className="text-blue-700 underline">{label}</a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function SubcontractorView({ payload }) {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  if (rows.length === 0) return <p className="text-sm text-gray-500">No subcontractor rows saved.</p>;
  return (
    <div className="space-y-3">
      {payload?.report_week && (
        <p className="text-xs text-gray-600">Week ending: <strong>{payload.report_week}</strong></p>
      )}
      <div className="overflow-auto border border-gray-200 rounded-lg">
        <table className="w-full text-xs">
          <thead style={{ background: '#003DA5', color: 'white' }}>
            <tr>
              <th className="px-2 py-1.5 text-left font-bold">#</th>
              <th className="px-2 py-1.5 text-left font-bold">Name</th>
              <th className="px-2 py-1.5 text-left font-bold">Scope of work</th>
              <th className="px-2 py-1.5 text-left font-bold">Contracts</th>
              <th className="px-2 py-1.5 text-left font-bold">Invoices</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.id || idx} className={idx % 2 ? 'bg-blue-50/30' : ''}>
                <td className="px-2 py-1 text-gray-500">{idx + 1}</td>
                <td className="px-2 py-1 text-gray-900 font-bold">{r.name}</td>
                <td className="px-2 py-1 text-gray-700">{r.scope_of_work}</td>
                <td className="px-2 py-1 text-gray-700">{(r.contracts || []).length} file(s)</td>
                <td className="px-2 py-1 text-gray-700">{(r.invoices || []).length} file(s)</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Renders a schedule snapshot — staff×day matrix exactly as authored
 *  inside the AI Schedule dialog. Uses the same matrix builder as the
 *  live editor so the on-screen view is identical to the export. */
function ScheduleView({ payload }) {
  const schedule = payload?.schedule;
  const team = payload?.team || [];
  const weekStart = payload?.week_start;
  if (!schedule || !weekStart) return <p className="text-sm text-gray-500">No schedule data saved.</p>;
  const accent = payload?.branding?.restaurant_accent || '#003DA5';
  const { rows: matrixRows, dayHeaders, matrix, totals } = buildScheduleMatrix({ schedule, team, weekStart });
  const fmtCell = (list) => (list || []).map((s) => `${(s.start_time || '').trim()}–${(s.end_time || '').trim()}`.replace(/^–$/, '')).filter(Boolean).join(' + ');
  return (
    <div className="space-y-3">
      {schedule.headline_metric && (
        <div className="p-3 rounded-xl" style={{ background: '#EAF2FF', border: '1px solid #C9D6E6' }}>
          <p className="text-sm font-bold" style={{ color: accent }}>{schedule.headline_metric}</p>
          {schedule.summary && <p className="text-xs text-gray-700 mt-1">{schedule.summary}</p>}
        </div>
      )}
      <div className="overflow-auto rounded-lg border" style={{ borderColor: '#E2DBC8' }}>
        <table className="w-full text-xs border-collapse">
          <thead style={{ background: accent, color: 'white' }}>
            <tr>
              <th className="px-2 py-2 text-left font-bold">Staff</th>
              <th className="px-2 py-2 text-left font-bold">Position</th>
              {dayHeaders.map((d) => (
                <th key={d.label} className="px-2 py-2 text-center font-bold">
                  {d.label}<span className="block text-[8px] font-normal opacity-85">{d.date.slice(5)}</span>
                </th>
              ))}
              <th className="px-2 py-2 text-center font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            {matrixRows.map((r, idx) => {
              const alt = idx % 2 === 1;
              return (
                <tr key={r.name} style={{ background: alt ? '#F4F8FD' : 'white' }}>
                  <td className="px-2 py-1.5 font-bold" style={{ color: '#1A2E2E' }}>{r.name}</td>
                  <td className="px-2 py-1.5 italic" style={{ color: '#4A5D5D' }}>{r.position || ''}</td>
                  {dayHeaders.map((dh) => {
                    const txt = fmtCell(matrix[r.name][dh.label]);
                    return (
                      <td key={dh.label} className="px-2 py-1.5 text-center" style={txt ? { color: '#1A2E2E' } : { color: '#B0B5BD', background: '#FAFAFA', fontStyle: 'italic' }}>
                        {txt || 'OFF'}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-center font-bold" style={{ color: accent }}>{totals[r.name] || 0} h</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────── print-friendly HTML builders ─────────────────────────

function buildBrandedHead(snapshot) {
  const b = (snapshot.payload && snapshot.payload.branding) || {};
  const hotelName = b.hotel_name || 'Dreams Rose Hall Resort & Spa';
  const hotelSub = b.hotel_subtitle || '';
  const accent = b.restaurant_accent || '#003DA5';
  const logos = [b.hotel_logo_url, b.hotel_operator_logo_url, b.restaurant_logo_url]
    .filter(Boolean)
    .map((u) => `<img src="${esc(absLogo(u))}" alt="logo" style="height:18mm;max-width:38mm;object-fit:contain;background:white;padding:2mm;border-radius:2mm"/>`)
    .join('<span style="display:inline-block;width:4mm"></span>');
  return `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10mm;border-bottom:1px solid #E2DBC8;padding-bottom:6mm;margin-bottom:6mm">
      <div>
        <p class="kicker" style="color:${esc(accent)};margin:0">${esc(hotelName.toUpperCase())}</p>
        ${b.restaurant_name ? `<p style="color:${esc(accent)};font-weight:700;margin:1mm 0 0 0;font-size:11px;letter-spacing:0.15em;text-transform:uppercase">${esc(b.restaurant_name)}</p>` : ''}
        ${hotelSub ? `<p style="margin:1mm 0 0 0;color:#666;font-size:10px">${esc(hotelSub)}</p>` : ''}
      </div>
      <div style="display:flex;align-items:center;gap:4mm">${logos}</div>
    </div>
    <p class="kicker" style="color:${esc(accent)}">${esc((KIND_LABELS[snapshot.kind] || snapshot.kind).toUpperCase())}</p>
    <h1 style="color:${esc(accent)}">${esc(snapshot.title)}</h1>
    <p class="meta">Saved by ${esc(snapshot.created_by_name || snapshot.created_by || 'Unknown')} · ${esc(fmtDate(snapshot.created_at))}</p>
  `;
}

function buildPrintHtml(snapshot) {
  const head = buildBrandedHead(snapshot);
  const p = snapshot.payload || {};

  if (snapshot.kind === 'inventory' || snapshot.kind === 'permit') {
    const rows = Array.isArray(p.rows) ? p.rows : [];
    if (rows.length === 0) return head + '<p>No rows saved.</p>';
    const cols = Array.from(
      rows.reduce((set, r) => { Object.keys(r || {}).forEach((k) => { if (k !== 'id' && k !== '_id' && k !== 'branding') set.add(k); }); return set; }, new Set()),
    );
    const colW = (100 / (cols.length + 1)).toFixed(2);
    const thead = `<tr><th style="width:6%">#</th>${cols.map((c) => `<th style="width:${colW}%">${esc(c.replace(/_/g, ' '))}</th>`).join('')}</tr>`;
    const tbody = rows.map((r, idx) => `<tr><td>${idx + 1}</td>${cols.map((c) => `<td>${esc(r[c] ?? '')}</td>`).join('')}</tr>`).join('');
    return head + `<table><thead>${thead}</thead><tbody>${tbody}</tbody></table>`;
  }

  if (snapshot.kind === 'schedule') {
    if (!p.schedule || !p.week_start) return head + '<p>No schedule saved.</p>';
    const accent = p.branding?.restaurant_accent || '#003DA5';
    return head + buildScheduleMatrixHtml({
      schedule: p.schedule,
      team: p.team || [],
      weekStart: p.week_start,
      deptLabel: p.dept_label || '',
      restaurant: p.branding?.restaurant_name ? { name: p.branding.restaurant_name } : null,
      logoUrl: '',
      accent,
    });
  }

  if (snapshot.kind === 'checklist') {
    const items = Array.isArray(p.items) ? p.items : [];
    const lis = items.map((it) => {
      const text = typeof it === 'string' ? it : (it?.text || it?.label || '');
      const done = typeof it === 'object' ? !!it.done : false;
      return `<li>${done ? '☑' : '☐'} ${esc(text)}</li>`;
    }).join('');
    return head + (p.subtitle ? `<p>${esc(p.subtitle)}</p>` : '') + `<ul>${lis}</ul>`;
  }

  if (snapshot.kind === 'insurer') {
    const photos = Array.isArray(p.photos) ? p.photos : [];
    const docs = Array.isArray(p.documents) ? p.documents : [];
    const links = Array.isArray(p.links) ? p.links : [];
    const BE = process.env.REACT_APP_BACKEND_URL;
    const abs = (u) => u && (u.startsWith('http') ? u : `${BE}${u}`);
    let body = '';
    if (p.notes) body += `<h2>Notes</h2><p>${esc(p.notes).replace(/\n/g, '<br/>')}</p>`;
    if (photos.length) body += `<h2>Photos (${photos.length})</h2><div class="grid">${photos.map((u) => `<img src="${esc(abs(u))}" alt="photo"/>`).join('')}</div>`;
    if (docs.length) body += `<h2>Documents (${docs.length})</h2><ul>${docs.map((u) => `<li>${esc(u.split('/').pop())}</li>`).join('')}</ul>`;
    if (links.length) body += `<h2>Links (${links.length})</h2><ul>${links.map((l) => { const url = typeof l === 'string' ? l : (l?.url || ''); const label = typeof l === 'object' ? (l.label || url) : url; return `<li>${esc(label)} — ${esc(url)}</li>`; }).join('')}</ul>`;
    return head + (body || '<p>Empty record.</p>');
  }

  if (snapshot.kind === 'subcontractor') {
    const rows = Array.isArray(p.rows) ? p.rows : [];
    const week = p.report_week ? `<p>Week ending: <strong>${esc(p.report_week)}</strong></p>` : '';
    const tbody = rows.map((r, idx) => `<tr><td>${idx + 1}</td><td>${esc(r.name)}</td><td>${esc(r.scope_of_work)}</td><td>${(r.contracts || []).length}</td><td>${(r.invoices || []).length}</td></tr>`).join('');
    return head + week + `<table><thead><tr><th style="width:6%">#</th><th style="width:25%">Name</th><th style="width:43%">Scope</th><th style="width:13%">Contracts</th><th style="width:13%">Invoices</th></tr></thead><tbody>${tbody}</tbody></table>`;
  }

  return head + `<pre>${esc(JSON.stringify(p, null, 2))}</pre>`;
}

// ───────────────────────── component ─────────────────────────

export default function SnapshotViewerDialog({ snapshot, open, onClose }) {
  const kind = snapshot?.kind;

  const view = useMemo(() => {
    if (!snapshot) return null;
    switch (kind) {
      case 'inventory':     return <InventoryView payload={snapshot.payload} />;
      case 'checklist':     return <ChecklistView payload={snapshot.payload} />;
      case 'permit':        return <PermitView payload={snapshot.payload} />;
      case 'insurer':       return <InsurerView payload={snapshot.payload} />;
      case 'subcontractor': return <SubcontractorView payload={snapshot.payload} />;
      case 'schedule':      return <ScheduleView payload={snapshot.payload} />;
      default:              return <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto">{JSON.stringify(snapshot.payload, null, 2)}</pre>;
    }
  }, [snapshot, kind]);

  const onPrint = () => {
    if (!snapshot) return;
    printHtml(buildPrintHtml(snapshot), snapshot.title);
  };

  const onRestore = () => {
    if (!snapshot) return;
    window.dispatchEvent(new CustomEvent('open-snapshot', { detail: snapshot }));
    onClose?.();
  };

  if (!snapshot) return null;
  const brand = snapshot.payload?.branding || {};
  const brandAccent = brand.restaurant_accent || '#003DA5';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="snapshot-viewer-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8" style={{ fontFamily: 'Manrope' }}>
            {kind === 'inventory' ? <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              : kind === 'schedule' ? <CalendarDays className="w-5 h-5" style={{ color: brandAccent }} />
              : <FileText className="w-5 h-5 text-blue-600" />}
            <span className="truncate" data-testid="snapshot-viewer-title">{snapshot.title}</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: `${brandAccent}20`, color: brandAccent }}>{KIND_LABELS[kind] || kind}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Branded header — hotel name + restaurant (when present) + small
            logos pulled from the snapshot's payload.branding. Same header
            renders in the print iframe so on-screen and printed views are
            visually identical. */}
        {(brand.hotel_name || brand.restaurant_name) && (
          <div
            data-testid="snapshot-viewer-brand"
            className="flex items-center justify-between gap-3 px-1 py-3"
            style={{ borderBottom: `1px solid ${brandAccent}30` }}
          >
            <div className="min-w-0">
              <p className="text-[9px] font-bold tracking-[0.25em] uppercase truncate" style={{ color: brandAccent, fontFamily: 'Manrope' }}>
                {brand.hotel_name || 'Dreams Rose Hall'}
              </p>
              {brand.restaurant_name && (
                <p className="text-xs font-bold mt-0.5 truncate" style={{ color: brandAccent, fontFamily: 'Manrope' }}>
                  {brand.restaurant_name}
                </p>
              )}
              {brand.hotel_subtitle && (
                <p className="text-[10px] mt-0.5 truncate" style={{ color: '#64748B', fontFamily: 'Manrope' }}>
                  {brand.hotel_subtitle}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {[brand.hotel_logo_url, brand.restaurant_logo_url].filter(Boolean).map((u, i) => (
                <img
                  key={i}
                  src={absLogo(u)}
                  alt="brand"
                  className="h-9 w-auto max-w-[80px] object-contain rounded-md"
                  style={{ background: 'white', padding: 2 }}
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 px-1 py-2 border-b border-gray-200">
          <button
            type="button"
            onClick={onPrint}
            data-testid="snapshot-viewer-print"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition hover:-translate-y-0.5"
            style={{ backgroundColor: brandAccent, color: 'white', fontFamily: 'Manrope' }}
            title="Print this saved file"
          >
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          {RESTORABLE_KINDS.has(kind) && (
            <button
              type="button"
              onClick={onRestore}
              data-testid="snapshot-viewer-restore"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition hover:-translate-y-0.5"
              style={{ backgroundColor: '#15803D20', color: '#15803D', border: '1px solid #BBF7D0', fontFamily: 'Manrope' }}
              title="Restore this snapshot into the live form (creates a new list)"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Restore into form
            </button>
          )}
          <span className="ml-auto text-[10px] text-gray-500 self-center">
            Saved by <strong>{snapshot.created_by_name || snapshot.created_by}</strong> · {fmtDate(snapshot.created_at)}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto py-3" data-testid="snapshot-viewer-body">
          {view}
        </div>
      </DialogContent>
    </Dialog>
  );
}
